import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ExportButton,
  GradeLevelOptions,
  GradeOptions,
  copyGradeAndSectionReport,
  exportGradeAndSectionReportToExcel,
  exportGradeAndSectionReportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/grade-and-section-report/GradeAndSectionReportAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { GradeAndSectionReportFilter } from "./grade-and-section-report-filter/GradeAndSectionReportFilter";
import { GradeAndSectionReportTable } from "./grade-and-section-report-table/GradeAndSectionReportTable";
import { useGradeAndSectionReportUIContext } from "./GradeAndSectionReportUIContext";

export function GradeAndSectionReportCard() {
  // Grade and Section Report UI Context
  const gradeAndSectionReportUIContext = useGradeAndSectionReportUIContext();
  const gradeAndSectionReportUIProps = useMemo(() => {
    return {
      columns: gradeAndSectionReportUIContext.columns,
      gradeStyle: gradeAndSectionReportUIContext.gradeStyle,
      setGradeStyle: gradeAndSectionReportUIContext.setGradeStyle,
      gradeLevelStyle: gradeAndSectionReportUIContext.gradeLevelStyle,
      queryParams: gradeAndSectionReportUIContext.queryParams
    };
  }, [gradeAndSectionReportUIContext]);
    
  // Getting curret state of Grade and Section Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.gradeAndSectionReport }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Grade and Section Report Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  // ...
  let rows = [];
  let data = [];
  const exportedColumns = gradeAndSectionReportUIProps.columns.filter(column => !column.hidden);
  const fields = exportedColumns.map(column => column.dataField);

  /** */
  const setLevelEntities = (level, entities) => {
    const levelEntities = entities.filter(entity => entity.gradeLevel === level);
            
    if (levelEntities.length > 0) {
      rows.push([
        GradeLevelOptions.find(option => option.value === level).label,
        "",
        levelEntities.reduce((sum, item) => sum + item.capacity, 0),
        levelEntities.reduce((sum, item) => sum + item.maleStudentCount, 0),
        levelEntities.reduce((sum, item) => sum + item.femaleStudentCount, 0),
        levelEntities.reduce((sum, item) => sum + item.reRegistratedStudentCount, 0),
        levelEntities.reduce((sum, item) => sum + item.newRegistratedStudentCount, 0),
        levelEntities.reduce((sum, item) => sum + item.totalStudentCount, 0)
      ]);

      levelEntities.forEach(entity => {
        const row = [];
        fields.forEach(field => row.push(entity[field] ?? ""));
        rows.push(row);
      });
    }
  }

  /** */
  const setGradeEntities = (grade, entities) => {
    const gradeEntities = entities.filter(entity => entity.grade === grade);

    if (gradeEntities.length > 0) {
      rows.push([
        gradeEntities[0].gradeLevelName,
        grade,
        "",
        gradeEntities.reduce((sum, item) => sum + item.capacity, 0),
        gradeEntities.reduce((sum, item) => sum + item.maleStudentCount, 0),
        gradeEntities.reduce((sum, item) => sum + item.femaleStudentCount, 0),
        gradeEntities.reduce((sum, item) => sum + item.reRegistratedStudentCount, 0),
        gradeEntities.reduce((sum, item) => sum + item.newRegistratedStudentCount, 0),
        gradeEntities.reduce((sum, item) => sum + item.totalStudentCount, 0)
      ]);

      gradeEntities.forEach(entity => {
        const row = [];
        fields.forEach(field => row.push(entity[field] ?? ""));
        rows.push(row);
      });
    }
  }

  /** */
  const getLevelRows = (column, level, entities) => {
    let levelRows = [];
    const levelEntities = entities.filter(entity => entity.gradeLevel === level);

    if (levelEntities.length > 0) {      
      if (column === "name") levelRows.push("");
      else if (column === "grade") levelRows.push(levelEntities[0].gradeLevelName);
      else levelRows.push(levelEntities.reduce((sum, item) => sum + item[column], 0));
      levelRows = levelRows.concat(levelEntities.map(entity => entity[column]));
    }

    return levelRows;
  }

  /** */
  const getGradeRows = (column, grade, entities) => {
    let gradeRows = [];
    const gradeEntities = entities.filter(entity => entity.grade === grade);

    if (gradeEntities.length > 0) {
      if (column === "name") gradeRows.push("");
      else if (column === "grade") gradeRows.push(grade);
      else if (column === "gradeLevelName") gradeRows.push(gradeEntities[0].gradeLevelName);
      else gradeRows.push(gradeEntities.reduce((sum, item) => sum + item[column], 0));
      gradeRows = gradeRows.concat(gradeEntities.map(entity => entity[column]));
    }

    return gradeRows;
  }

  /** */
  const setLevelData = (columns, level, entities) => {    
    const levelEntities = entities.filter(entity => entity.gradeLevel === level);

    if (levelEntities.length > 0) {
      let row = {};

      columns.forEach(column => {
        if (column.text === "Şube") row[column.text] = "";
        else if (column.text === "Sınıf") row[column.text] = "<b>" + levelEntities[0].gradeLevelName + "</b>";
        else row[column.text] = "<b>" + levelEntities.reduce((sum, item) => sum + item[column.dataField], 0) + "</b>";        
      });

      data.push(row);

      levelEntities.forEach(entity => {
        row = {};
        exportedColumns.forEach(column => row[column.text] = entity[column.dataField] ?? "");
        data.push(row);
      });
    }
  }

  /** */
  const setGradeData = (columns, grade, entities) => {    
    const gradeEntities = entities.filter(entity => entity.grade === grade);

    if (gradeEntities.length > 0) {
      let row = {};

      columns.forEach(column => {
        if (column.text === "Şube") row[column.text] = "";
        else if (column.text === "Sınıf") row[column.text] = "<b>" + grade + "</b>";
        else if (column.text === "Sınıf Düzeyi") row[column.text] = "<b>" + gradeEntities[0].gradeLevelName + "</b>";
        else row[column.text] = "<b>" + gradeEntities.reduce((sum, item) => sum + item[column.dataField], 0) + "</b>";        
      });

      data.push(row);

      gradeEntities.forEach(entity => {
        row = {};
        exportedColumns.forEach(column => row[column.text] = entity[column.dataField] ?? "");
        data.push(row);
      });
    }
  }
    
  /** Belli Bir Sınıf ve Şube Raporunu Excele Aktarır */
  const exportToExcel = () => {
    dispatch(actions.getAllGradeAndSectionReport(gradeAndSectionReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;      
      const widths = exportedColumns.map(column => column.width);
      const columns = exportedColumns.map(column => column.text);

      if (gradeAndSectionReportUIProps.gradeLevelStyle === "light") {
        if (gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
          const gradeLevels = [...new Set(entities.map(entity => entity.gradeLevel))];
          gradeLevels.forEach(level => setLevelEntities(level, entities));

        } else {
          GradeLevelOptions.map(option => option.value).forEach(value => setLevelEntities(value, entities));
        }

      } else if (gradeAndSectionReportUIProps.gradeStyle === "light") {
        if (gradeAndSectionReportUIProps.queryParams.sortField === "gradeLevelName" || gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
          const grades = [...new Set(entities.map(entity => entity.grade))];
          grades.forEach(grade => setGradeEntities(grade, entities));

        } else {
          GradeOptions.map(option => option.value).forEach(value => setGradeEntities(value, entities));
        }

      } else {
        entities.forEach(entity => {
          const row = [];
          fields.forEach(field => row.push(entity[field]));
          rows.push(row);
        });
      }

      const filename = gradeAndSectionReportUIProps.queryParams.term + " - " + (gradeAndSectionReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Sınıf ve Şube Raporu";
      const sheetname = "Sınıf ve Şube Raporu";
      exportGradeAndSectionReportToExcel({ columns, rows, widths, filename, sheetname });
    });    
  }

  /** Belli Bir Sınıf ve Şube Raporunu PDF e Aktarır */
  const exportToPDF = () => {
    dispatch(actions.getAllGradeAndSectionReport(gradeAndSectionReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [];

      exportedColumns.forEach(column => {
        let rows = [];

        if (gradeAndSectionReportUIProps.gradeLevelStyle === "light") {
          if (gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
            const gradeLevels = [...new Set(entities.map(entity => entity.gradeLevel))];
            gradeLevels.forEach(grade => {
              const levelRows = getLevelRows(column.dataField, grade, entities);
              rows = rows.concat(levelRows);
            });

          } else {
            GradeLevelOptions.map(option => option.value).forEach(value => {
              const levelRows = getLevelRows(column.dataField, value, entities);
              rows = rows.concat(levelRows);
            });
          }

        } else if (gradeAndSectionReportUIProps.gradeStyle === "light") {
          if (gradeAndSectionReportUIProps.queryParams.sortField === "gradeLevelName" || gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
            const grades = [...new Set(entities.map(entity => entity.grade))];
            grades.forEach(grade => {
              const gradeRows = getGradeRows(column.dataField, grade, entities);
              rows = rows.concat(gradeRows);
            });

          } else {
            GradeOptions.map(option => option.value).forEach(value => {
              const gradeRows = getGradeRows(column.dataField, value, entities);
              rows = rows.concat(gradeRows);
            });
          }

        } else {
          rows = entities.map(entity => entity[column.dataField]);
        }

        const view = {
          header: column.text,
          rows: rows
        };
        views.push(view);
      });
  
      const filename = gradeAndSectionReportUIProps.queryParams.term + " - " + (gradeAndSectionReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Sınıf ve Şube Raporu";
      exportGradeAndSectionReportToPDF(views, filename);
    });
  }

  /** Belli Bir Sınıf ve Şube Raporunu Yazdırma Penceresini Açar */
  const printGradeAndSectionReport = () => {
    dispatch(actions.getAllGradeAndSectionReport(gradeAndSectionReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const header = gradeAndSectionReportUIProps.queryParams.term + " - " + (gradeAndSectionReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Sınıf ve Şube Raporu";
      const properties = exportedColumns.map(column => column.text);

      if (gradeAndSectionReportUIProps.gradeLevelStyle === "light") {
        if (gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
          const gradeLevels = [...new Set(entities.map(entity => entity.gradeLevel))];
          gradeLevels.forEach(level => setLevelData(exportedColumns, level, entities));

        } else {
          GradeLevelOptions.map(option => option.value).forEach(value => setLevelData(exportedColumns, value, entities));
        }

      } else if (gradeAndSectionReportUIProps.gradeStyle === "light") {
        if (gradeAndSectionReportUIProps.queryParams.sortField === "gradeLevelName" || gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
          const grades = [...new Set(entities.map(entity => entity.grade))];
          grades.forEach(grade => setGradeData(exportedColumns, grade, entities));

        } else {
          GradeOptions.map(option => option.value).forEach(value => setGradeData(exportedColumns, value, entities));
        }

      } else {
        entities.forEach(entity => {
          const row = {};
          exportedColumns.forEach(column => row[column.text] = entity[column.dataField] ?? "");
          data.push(row);
        });
      }      

      print({ data, properties, header });
    });
  }

  /** Belli Bir Sınıf ve Şube Raporunu Panoya Kopyalar */
  const copy = () => {
    dispatch(actions.getAllGradeAndSectionReport(gradeAndSectionReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const columns = exportedColumns.map(column => column.text);
      const header = gradeAndSectionReportUIProps.queryParams.term + " - " + (gradeAndSectionReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Sınıf ve Şube Raporu";

      if (gradeAndSectionReportUIProps.gradeLevelStyle === "light") {
        if (gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
          const gradeLevels = [...new Set(entities.map(entity => entity.gradeLevel))];
          gradeLevels.forEach(level => setLevelEntities(level, entities));

        } else {
          GradeLevelOptions.map(option => option.value).forEach(value => setLevelEntities(value, entities));
        }

      } else if (gradeAndSectionReportUIProps.gradeStyle === "light") {
        if (gradeAndSectionReportUIProps.queryParams.sortField === "gradeLevelName" || gradeAndSectionReportUIProps.queryParams.sortField === "grade") {
          const grades = [...new Set(entities.map(entity => entity.grade))];
          grades.forEach(grade => setGradeEntities(grade, entities));

        } else {
          GradeOptions.map(option => option.value).forEach(value => setGradeEntities(value, entities));
        }

      } else {
        entities.forEach(entity => {
          const row = [];
          fields.forEach(field => row.push(entity[field] ?? ""));
          rows.push(row);
        });
      }

      copyGradeAndSectionReport({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Sınıf ve Şube Raporu">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportToExcel}
              exportToPDF={exportToPDF}
              print={printGradeAndSectionReport}
              copy={copy}
            />            
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <GradeAndSectionReportFilter />
        <GradeAndSectionReportTable />
      </CardBody>
    </Card>
  );
}